import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const API = "https://wwwa.isepsantafe.net.ar/expedientes";

const GrabadorVideoEntrevista = ({
  dni,
  procesoId,
  itemId,
  onVideoSubido,
  maxSegundos = 180,
}) => {
  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [permisoCamara, setPermisoCamara] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [segundos, setSegundos] = useState(0);
  const [subiendo, setSubiendo] = useState(false);

  const tiempoRestante = maxSegundos - segundos;

  useEffect(() => {
    return () => {
      detenerCamara();
      limpiarTimer();

      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const limpiarTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const solicitarCamara = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: true,
      });

      streamRef.current = stream;

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      setPermisoCamara(true);
    } catch (error) {
      console.error("Error accediendo a cámara/micrófono:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo acceder a la cámara",
        text: "Verificá los permisos de cámara y micrófono del navegador.",
      });
    }
  };

  const detenerCamara = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }

    setPermisoCamara(false);
  };

  const obtenerMimeType = () => {
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")) {
      return "video/webm;codecs=vp8,opus";
    }

    if (MediaRecorder.isTypeSupported("video/webm")) {
      return "video/webm";
    }

    if (MediaRecorder.isTypeSupported("video/mp4")) {
      return "video/mp4";
    }

    return "";
  };

  const iniciarGrabacion = async () => {
    try {
      if (!streamRef.current) {
        await solicitarCamara();
      }

      chunksRef.current = [];
      setVideoBlob(null);
      setVideoUrl("");
      setSegundos(0);

      const mimeType = obtenerMimeType();

      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 900000,
        audioBitsPerSecond: 64000,
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeType || "video/webm",
        });

        const url = URL.createObjectURL(blob);

        setVideoBlob(blob);
        setVideoUrl(url);
        setGrabando(false);
        limpiarTimer();
        detenerCamara();
      };

      mediaRecorder.start(1000);
      setGrabando(true);

      timerRef.current = setInterval(() => {
        setSegundos((prev) => {
          const nuevo = prev + 1;

          if (nuevo >= maxSegundos) {
            detenerGrabacion();
          }

          return nuevo;
        });
      }, 1000);
    } catch (error) {
      console.error("Error iniciando grabación:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo iniciar la grabación",
        text: error.message || "Intentá nuevamente.",
      });
    }
  };

  const detenerGrabacion = () => {
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }

      limpiarTimer();
      setGrabando(false);
    } catch (error) {
      console.error("Error deteniendo grabación:", error);
    }
  };

  const descartarVideo = async () => {
    const confirma = await Swal.fire({
      icon: "warning",
      title: "¿Descartar video?",
      text: "Se eliminará la grabación actual y podrás grabar nuevamente.",
      showCancelButton: true,
      confirmButtonText: "Sí, descartar",
      cancelButtonText: "Cancelar",
    });

    if (!confirma.isConfirmed) return;

    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }

    setVideoBlob(null);
    setVideoUrl("");
    setSegundos(0);
    chunksRef.current = [];
  };

  const subirVideo = async () => {
    try {
      if (!videoBlob) {
        Swal.fire({
          icon: "warning",
          title: "No hay video para enviar",
        });
        return;
      }

      if (!dni || !procesoId || !itemId) {
        Swal.fire({
          icon: "error",
          title: "Faltan datos",
          text: "No se encontró DNI, proceso o item.",
        });
        return;
      }

      const confirma = await Swal.fire({
        icon: "question",
        title: "¿Enviar entrevista?",
        text: "Una vez enviada, quedará cargada para revisión.",
        showCancelButton: true,
        confirmButtonText: "Sí, enviar",
        cancelButtonText: "Cancelar",
      });

      if (!confirma.isConfirmed) return;

      setSubiendo(true);

      const extension = videoBlob.type.includes("mp4") ? "mp4" : "webm";
      const archivo = new File(
        [videoBlob],
        `entrevista_${dni}.${extension}`,
        {
          type: videoBlob.type || "video/webm",
        }
      );

      const formData = new FormData();
      formData.append("video", archivo);
      formData.append("dni", dni);
      formData.append("proceso_id", procesoId);
      formData.append("item_id", itemId);

      const response = await axios.post(
        `${API}/ingresos/persona-item/upload-video-entrevista`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.ok) {
        Swal.fire({
          icon: "success",
          title: "Video enviado",
          text: "La entrevista fue cargada correctamente.",
        });

        if (onVideoSubido) {
          onVideoSubido(response.data);
        }
      } else {
        throw new Error(response.data?.mensaje || "No se pudo subir el video");
      }
    } catch (error) {
      console.error("Error subiendo video:", error);

      Swal.fire({
        icon: "error",
        title: "Error al subir video",
        text:
          error.response?.data?.mensaje ||
          error.message ||
          "Intentá nuevamente.",
      });
    } finally {
      setSubiendo(false);
    }
  };

  const formatearTiempo = (totalSegundos) => {
    const min = Math.floor(totalSegundos / 60);
    const seg = totalSegundos % 60;

    return `${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`;
  };

  return (
    <div style={styles.contenedor}>
      <h3 style={styles.titulo}>Entrevista en video</h3>

      <p style={styles.descripcion}>
        Grabá una entrevista de hasta {Math.floor(maxSegundos / 60)} minutos.
        Revisá el video antes de enviarlo.
      </p>

      <div style={styles.videoBox}>
        {!videoUrl ? (
          <video
            ref={videoPreviewRef}
            autoPlay
            muted
            playsInline
            style={styles.video}
          />
        ) : (
          <video
            src={videoUrl}
            controls
            style={styles.video}
          />
        )}
      </div>

      <div style={styles.estadoBox}>
        {grabando ? (
          <span style={styles.grabando}>
            ● Grabando - restante {formatearTiempo(tiempoRestante)}
          </span>
        ) : videoBlob ? (
          <span style={styles.listo}>Video listo para enviar</span>
        ) : permisoCamara ? (
          <span style={styles.preparado}>Cámara lista</span>
        ) : (
          <span style={styles.neutro}>Cámara no iniciada</span>
        )}
      </div>

      <div style={styles.botones}>
        {!permisoCamara && !videoBlob && !grabando && (
          <button
            type="button"
            onClick={solicitarCamara}
            style={styles.botonSecundario}
          >
            Activar cámara
          </button>
        )}

        {!grabando && !videoBlob && (
          <button
            type="button"
            onClick={iniciarGrabacion}
            style={styles.botonPrincipal}
          >
            Iniciar grabación
          </button>
        )}

        {grabando && (
          <button
            type="button"
            onClick={detenerGrabacion}
            style={styles.botonPeligro}
          >
            Detener
          </button>
        )}

        {videoBlob && !grabando && (
          <>
            <button
              type="button"
              onClick={descartarVideo}
              disabled={subiendo}
              style={styles.botonSecundario}
            >
              Grabar de nuevo
            </button>

            <button
              type="button"
              onClick={subirVideo}
              disabled={subiendo}
              style={styles.botonPrincipal}
            >
              {subiendo ? "Enviando..." : "Enviar entrevista"}
            </button>
          </>
        )}
      </div>

      {videoBlob && (
        <div style={styles.info}>
          Tamaño aproximado: {(videoBlob.size / 1024 / 1024).toFixed(2)} MB
        </div>
      )}
    </div>
  );
};

const styles = {
  contenedor: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    padding: "20px",
    borderRadius: "14px",
    background: "#ffffff",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
  titulo: {
    margin: "0 0 8px",
    fontSize: "22px",
    fontWeight: "700",
    color: "#1f2937",
  },
  descripcion: {
    margin: "0 0 16px",
    color: "#4b5563",
    fontSize: "14px",
  },
  videoBox: {
    width: "100%",
    background: "#111827",
    borderRadius: "12px",
    overflow: "hidden",
    minHeight: "320px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  video: {
    width: "100%",
    maxHeight: "420px",
    background: "#111827",
  },
  estadoBox: {
    marginTop: "12px",
    textAlign: "center",
    fontWeight: "600",
  },
  grabando: {
    color: "#dc2626",
  },
  listo: {
    color: "#15803d",
  },
  preparado: {
    color: "#2563eb",
  },
  neutro: {
    color: "#6b7280",
  },
  botones: {
    marginTop: "16px",
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  botonPrincipal: {
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
  },
  botonSecundario: {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "10px 16px",
    background: "#f9fafb",
    color: "#111827",
    fontWeight: "700",
    cursor: "pointer",
  },
  botonPeligro: {
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    background: "#dc2626",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
  },
  info: {
    marginTop: "12px",
    textAlign: "center",
    fontSize: "13px",
    color: "#6b7280",
  },
};

export default GrabadorVideoEntrevista;